import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_URL } from '../Config';
import { Calendar, Clock, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';

const NouvelleDemandeConge = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [soldeInfo, setSoldeInfo] = useState({ normal: 0, exceptionnel: 0 });
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [formData, setFormData] = useState({
    type: 'normal',
    date_debut: '',
    date_fin: '',
    motif: '',
    exception_details: [{ jour: '', motif_jour: '' }],
  });

  useEffect(() => {
    fetchSoldeConges();
  }, []);

  const fetchSoldeConges = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/solde-conges/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      const data = await response.json();
      setSoldeInfo(data);
    } catch (error) {
      console.error('Erreur chargement solde:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleExceptionDetailChange = (index, field, value) => {
    const newDetails = [...formData.exception_details];
    newDetails[index][field] = value;
    setFormData((prev) => ({ ...prev, exception_details: newDetails }));
  };

  const addExceptionDetail = () => {
    setFormData((prev) => ({
      ...prev,
      exception_details: [...prev.exception_details, { jour: '', motif_jour: '' }],
    }));
  };

  const removeExceptionDetail = (index) => {
    if (formData.exception_details.length > 1) {
      setFormData((prev) => ({
        ...prev,
        exception_details: prev.exception_details.filter((_, i) => i !== index),
      }));
    }
  };

  const calculateDuree = () => {
    if (!formData.date_debut || !formData.date_fin) return 0;
    const start = new Date(formData.date_debut);
    const end = new Date(formData.date_fin);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  const validateDates = () => {
    if (!formData.date_debut || !formData.date_fin) {
      return { valid: false, message: 'Veuillez renseigner les dates' };
    }

    const debut = new Date(formData.date_debut);
    const fin = new Date(formData.date_fin);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (debut < today) return { valid: false, message: 'Date de début invalide' };
    if (debut > fin) return { valid: false, message: 'Dates incohérentes' };

    return { valid: true };
  };

  const getSoldeRestant = () => {
    const duree = calculateDuree();
    const soldeTotal = formData.type === 'normal' ? soldeInfo.normal : soldeInfo.exceptionnel;
    return {
      soldeTotal,
      dureeDemandee: duree,
      soldeRestant: soldeTotal - duree
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (user.role === 'admin') {
        toast.error('Action non autorisée');
        setLoading(false);
        return;
      }

      const dateValidation = validateDates();
      if (!dateValidation.valid) {
        toast.error(dateValidation.message);
        setLoading(false);
        return;
      }

      if (!formData.motif.trim()) {
        toast.error('Veuillez indiquer un motif');
        setLoading(false);
        return;
      }

      if (formData.type === 'exceptionnel') {
        const hasEmptyDetail = formData.exception_details.some(
          (detail) => !detail.jour || !detail.motif_jour.trim()
        );
        if (hasEmptyDetail) {
          toast.error('Complétez tous les détails exceptionnels');
          setLoading(false);
          return;
        }
      }

      const solde = getSoldeRestant();
      if (solde.soldeRestant < 0) {
        toast.error(`Solde insuffisant. Disponible: ${solde.soldeTotal} jours, Demandé: ${solde.dureeDemandee} jours`);
        setLoading(false);
        return;
      }

      const payload = {
        type: formData.type,
        date_debut: formData.date_debut,
        date_fin: formData.date_fin,
        motif: formData.motif
      };

      if (formData.type === 'exceptionnel') {
        payload.exception_details = formData.exception_details;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/conges`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Erreur serveur');

      toast.success('Demande créée avec succès');
      navigate('/mes-conges');
      
    } catch (error) {
      toast.error('Erreur lors de la soumission');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const solde = getSoldeRestant();

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Nouvelle demande de congé</h1>
            <p className="text-gray-500 text-sm mt-1">
              Remplissez le formulaire ci-dessous pour soumettre une demande de congé
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Solde Section */}
          <div className="md:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                Votre solde
              </h2>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Congés normaux</span>
                    <span className="text-sm font-medium text-gray-900">{soldeInfo.normal} jours</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(100, soldeInfo.normal)}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">Congés exceptionnels</span>
                    <span className="text-sm font-medium text-gray-900">{soldeInfo.exceptionnel} jours</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(100, soldeInfo.exceptionnel)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center text-sm text-gray-600 mb-2">
                    <Clock className="w-4 h-4 mr-2" />
                    Durée demandée
                  </div>
                  <div className="text-2xl font-bold text-gray-900 text-center">
                    {calculateDuree()} <span className="text-sm font-normal text-gray-600">jours</span>
                  </div>
                </div>

                {solde.soldeRestant < 0 && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-md">
                    <div className="flex items-center">
                      <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                      <span className="text-sm text-red-700">Solde insuffisant</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="md:col-span-2">
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="border-b border-gray-200 px-6 py-4">
                <h2 className="text-lg font-medium text-gray-900">Formulaire de demande</h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Type de congé */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Type de congé <span style={{"color":"red"}}>*</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      formData.type === 'normal' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="type"
                        value="normal"
                        checked={formData.type === 'normal'}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="flex items-center">
                        <div className={`w-4 h-4 border rounded-full mr-3 flex items-center justify-center ${
                          formData.type === 'normal' 
                            ? 'border-blue-500' 
                            : 'border-gray-300'
                        }`}>
                          {formData.type === 'normal' && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Congé normal</div>
                          <div className="text-sm text-gray-500 mt-1">Congés annuels</div>
                        </div>
                      </div>
                    </label>

                    <label className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      formData.type === 'exceptionnel' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <input
                        type="radio"
                        name="type"
                        value="exceptionnel"
                        checked={formData.type === 'exceptionnel'}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className="flex items-center">
                        <div className={`w-4 h-4 border rounded-full mr-3 flex items-center justify-center ${
                          formData.type === 'exceptionnel' 
                            ? 'border-blue-500' 
                            : 'border-gray-300'
                        }`}>
                          {formData.type === 'exceptionnel' && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Congé exceptionnel</div>
                          <div className="text-sm text-gray-500 mt-1">Événements spéciaux</div>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Période */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Période</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">
                        Date de début <span style={{"color":"red"}}>*</span>
                      </label>
                      <input
                        type="date"
                        name="date_debut"
                        value={formData.date_debut}
                        onChange={handleChange}
                        min={today}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-600 mb-2">
                        Date de fin <span style={{"color":"red"}}>*</span>
                      </label>
                      <input
                        type="date"
                        name="date_fin"
                        value={formData.date_fin}
                        onChange={handleChange}
                        min={formData.date_debut || today}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>
                </div>
                {/* Motif */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Motif <span style={{"color":"red"}}>*</span>
                  </label>
                  <textarea
                    name="motif"
                    value={formData.motif}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                    placeholder="Décrivez le motif de votre congé..."
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Cette information est nécessaire pour le traitement de votre demande
                  </p>
                </div>

                {/* Détails exceptionnels */}
                {formData.type === 'exceptionnel' && (
                  <div className="border border-gray-200 rounded-lg p-5">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h3 className="font-medium text-gray-900">Détails exceptionnels</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Précisez les dates et motifs spécifiques
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={addExceptionDetail}
                        className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Ajouter un jour
                      </button>
                    </div>

                    <div className="space-y-4">
                      {formData.exception_details.map((detail, index) => (
                        <div key={index} className="border border-gray-200 rounded p-4">
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-sm font-medium text-gray-700">
                              Jour {index + 1}
                            </span>
                            {formData.exception_details.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeExceptionDetail(index)}
                                className="text-sm text-gray-400 hover:text-red-500"
                              >
                                Supprimer
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1.5">Date <span style={{"color":"red"}}>*</span></label>
                              <input
                                type="date"
                                value={detail.jour}
                                min={formData.date_debut}
                                max={formData.date_fin}
                                onChange={(e) => handleExceptionDetailChange(index, 'jour', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1.5">Motif spécifique <span style={{"color":"red"}}>*</span></label>
                              <input
                                type="text"
                                value={detail.motif_jour}
                                onChange={(e) => handleExceptionDetailChange(index, 'motif_jour', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                placeholder="Motif pour ce jour"
                                required
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Validation */}
                <div className="pt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="text-sm text-gray-500">
                      Votre demande sera soumise pour validation
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => navigate('/mes-conges')}
                        className="px-4 py-2.5 text-sm border border-gray-300 text-gray-700 rounded hover:"
                        disabled={loading}
                      >
                        Annuler
                      </button>
                      
                      <button
                        type="submit"
                        disabled={loading || solde.soldeRestant < 0}
                        className={`px-5 py-2.5 text-sm font-medium rounded ${
                          solde.soldeRestant < 0 || loading
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {loading ? (
                          <span className="flex items-center">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            En cours...
                          </span>
                        ) : (
                          'Soumettre la demande'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NouvelleDemandeConge;